import React, { useCallback, useState } from 'react';
import {
  View,
  Text,
  Modal,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { useTranslation } from '@oolio-group/localization';
import SetTimerModalStyles from './SetTimerModal.styles';
import { useScale } from '../../../hooks/useScale';
import { KdsTimerMilestone } from '@oolio-group/domain';
import InputText from '../../Input/InputText';
import SelectColour from '../../Select/SelectColour';
import Button from '../../Buttons/ButtonText';

export enum TimerAction {
  CREATE = 'create',
  EDIT = 'edit',
}

export interface SetTimerModalProps {
  visible: boolean;
  action: TimerAction;
  onClose?: () => void | Promise<void>;
  onDelete?: () => void | Promise<void>;
  onSave: (milestones: KdsTimerMilestone, index?: number) => void;
  milestone?: KdsTimerMilestone;
}

const SetTimerModal: React.FC<SetTimerModalProps> = ({
  visible,
  action,
  onClose = () => undefined,
  onDelete = () => undefined,
  onSave,
  milestone: existingMilestone,
}) => {
  const { translate } = useTranslation();
  const styles = SetTimerModalStyles();
  const { scale } = useScale();
  const [milestone, setMilestone] = useState<KdsTimerMilestone>(
    existingMilestone ?? { label: '', minutes: NaN, color: '' },
  );

  const disableSaveBtn =
    !milestone.label.trim().length ||
    !milestone.color.trim().length ||
    !!!milestone.minutes;

  const onPressSave = useCallback(() => {
    onSave(milestone);
  }, [onSave, milestone]);
  const onPressDelete = useCallback(() => {
    onDelete();
  }, [onDelete]);

  const onChangeInput = (key: string, value: string | number) => {
    setMilestone(prev => {
      return { ...prev, [key]: value };
    });
  };

  return (
    <Modal
      animationType="fade"
      onDismiss={onClose}
      onRequestClose={onClose}
      presentationStyle="overFullScreen"
      transparent={true}
      visible={visible}
      supportedOrientations={['landscape', 'landscape-left', 'landscape-right']}
    >
      <KeyboardAvoidingView
        style={styles.background}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <View style={styles.container}>
          <Text style={styles.title}>
            {translate(
              action === TimerAction.CREATE
                ? 'kitchenDisplay.createTimer'
                : 'kitchenDisplay.editTimer',
            )}
          </Text>
          <View style={styles.inputs}>
            <InputText
              onChangeText={t => onChangeInput('label', t)}
              value={milestone.label}
              containerStyle={styles.inputContainer}
              testID={'input-label'}
              placeholder={'Enter Name...'}
              title={translate('kitchenDisplay.name')}
            />
            <InputText
              onChangeText={t => onChangeInput('minutes', parseFloat(t))}
              value={isNaN(milestone.minutes) ? '' : milestone.minutes.toString()}
              containerStyle={styles.inputContainer}
              testID={'input-minutes'}
              placeholder={'Enter time in mins...'}
              title={translate('kitchenDisplay.timer')}
              numericOnly
            />
            <SelectColour
              testID="select-colour"
              title={translate('kitchenDisplay.color')}
              value={milestone.color}
              onChange={color => onChangeInput('color', color)}
              containerStyle={styles.inputContainer}
            />
          </View>
          <View style={styles.actions}>
            <Button
              testID={'btn-save'}
              label={translate('button.confirm')}
              type={disableSaveBtn ? 'interface' : 'positive'}
              onPress={onPressSave}
              disabled={disableSaveBtn}
            />
            {action === TimerAction.EDIT && (
              <Button
                testID={'btn-delete'}
                label={translate('button.delete')}
                type="negativeLight"
                onPress={onPressDelete}
              />
            )}
            <Button
              testID={'btn-dismiss'}
              label={translate('button.dismiss')}
              type="active"
              onPress={onClose}
            />
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
};

export default SetTimerModal;
